import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const BracketNavBar = ({
    picksInfo,
    roundDash,
    setRoundDash,
    setRoundDashPosition,
    bracketState,
    hasBracketDeadlinePassed,
    seriesLengths,
    usingSeriesLengths,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleNavClick = (roundDashValue) => {
        // First update the roundDash state (which also updates URL)
        setRoundDash(roundDashValue);
        
        // Use a small timeout to ensure the state is updated before scrolling
        setTimeout(() => {
            // Then call the corresponding scroll function
            const scrollFunction = setRoundDashPosition[roundDashValue];
            if (scrollFunction && typeof scrollFunction === 'function') {
                scrollFunction();
            }
        }, 50);
    };

    const howManyPicksForRound = (round) => {
        let picksMade = 0;
        const picksToMake = picksInfo?.find(p => p.round === round)?.picks || 0;
        
        // Get all bracket picks for this round
        const roundPicks = bracketState?.filter(pick => pick.round === round) || [];
        
        if (usingSeriesLengths) {
            // Only count picks that have both bracket pick and series length
            picksMade = roundPicks?.filter(bracketPick => {
                // Check if there's a matching series length pick for this matchupId
                return seriesLengths?.some(seriesLength => 
                    seriesLength.matchupId === bracketPick.matchupId && 
                    seriesLength.round === bracketPick.round
                );
            })?.length;
        } else {
            // If not using series lengths, just count the bracket picks
            picksMade = roundPicks?.length;
        }
        
        return { picksMade, picksToMake };
    };

    // Create array of navigation items based on picksInfo
    const navItems = picksInfo?.map(info => ({
        round: info.round,
        roundDash: info.round, // Setting roundDash equal to round
        name: info.name || `Round ${info.round}` // Use the name from picksInfo if available
    })) || [];

    return (
        <Box sx={{ 
            display: "flex", 
            flexDirection: "column",
            justifyContent: "space-evenly", 
            alignItems: "center", 
            padding: '8px 16px', 
            backgroundColor: '#FFF', 
            boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' 
        }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: isMobile ? '99%' : '90%',
                    alignItems: 'center',
                    margin: '0 auto',
                }}
            >
                {navItems?.map((item, index) => {
                    const isSelected = roundDash === item.roundDash;
                    const pickInfo = howManyPicksForRound(item.round);
                    
                    return (
                        <Button
                            key={index}
                            onClick={() => handleNavClick(item.roundDash)}
                            sx={{
                                textTransform: "none",
                                fontSize: "12px",
                                fontWeight: 700,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "8px 8px",
                                borderRadius: isSelected ? "4px" : "0px",
                                backgroundColor: isSelected ? "#D9D9D9" : "transparent",
                                fontFamily: "inherit",
                                transition: "background-color 0.3s ease",
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                ":hover": {
                                    backgroundColor: isSelected ? "#D9D9D9" : "transparent", 
                                },
                            }}
                        >
                            {/* First line always #002129 */}
                            <Typography sx={{ color: "#002129", fontSize: "12px", fontWeight: 700 }}>
                                {item.name}
                            </Typography>

                            {/* Second line with conditional color */}
                            {!hasBracketDeadlinePassed && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Typography 
                                        sx={{ 
                                            fontSize: '12px', 
                                            fontWeight: '700', 
                                            color: pickInfo?.picksMade === pickInfo?.picksToMake ? '#00AA72' : '#CC293C' 
                                        }}
                                    >
                                        {pickInfo?.picksMade}/{pickInfo?.picksToMake}
                                    </Typography>
                                    {pickInfo?.picksMade === pickInfo?.picksToMake ? (
                                        <CheckIcon sx={{ color: '#00AA72', width: '16px', height: '16px' }} />
                                    ) : (
                                        <CloseIcon sx={{ color: '#CC293C', width: '16px', height: '16px' }} />
                                    )}
                                </Box>
                            )}
                        </Button>
                    );
                })}
            </Box>
        </Box>
    );
};

export default BracketNavBar;